/**
 * Hook that translates a GenericWidgetConfig into a performance API call.
 *
 * Handles:
 *  - Converting hierarchy dimensions (agency, macom, subcom1) into
 *    the hierarchy_level API param
 *  - Building group_by and filter params
 *  - Re-fetching when config changes
 *  - Race condition prevention via generation counter
 */
import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { api } from '../../../services/api'
import type { GenericWidgetConfig, PerfRow } from './types'
import { HIERARCHY_DIMENSIONS, GEO_DIMENSIONS } from './types'

interface PerfResponse {
  results: PerfRow[]
  meta: { group_by: string[]; row_count: number; hierarchy_level?: string }
}

interface UsePerformanceQueryResult {
  data: PerfRow[]
  loading: boolean
  error: string | null
  /** Bust the in-memory cache for this query and re-fetch from the server. */
  refresh: () => void
}

/** Decide whether a config should route to the geo-performance endpoint. */
function isGeoQuery(config: GenericWidgetConfig): boolean {
  if (GEO_DIMENSIONS.has(config.group_by)) return true
  if (config.series_by && GEO_DIMENSIONS.has(config.series_by)) return true
  const hasStates = Array.isArray(config.state_code)
    ? config.state_code.length > 0
    : !!config.state_code
  if (hasStates || config.congressional_district || config.location_kind) return true
  return false
}

/** Route the Time-to-Contract metric to the query-time percentile endpoint
 *  (record-level far_ot_speed_to_award), not performance_fact. */
function isSpeedQuery(config: GenericWidgetConfig): boolean {
  return config.metrics?.includes('avg_days_to_award') ?? false
}

/**
 * Build the API query params from a widget config + optional drill-down overrides.
 */
function buildParams(
  config: GenericWidgetConfig,
  drillFilters?: Record<string, string | number>,
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {}

  if (isGeoQuery(config)) {
    return buildGeoParams(config, drillFilters)
  }

  const groupBy = config.group_by
  const seriesBy = config.series_by
  const groupByIsHierarchy = HIERARCHY_DIMENSIONS.has(groupBy)
  const seriesByIsHierarchy = seriesBy ? HIERARCHY_DIMENSIONS.has(seriesBy) : false

  if (groupByIsHierarchy || seriesByIsHierarchy) {
    // At least one dimension is a hierarchy level (needs dim_offices JOIN).
    //
    // Cases:
    //   1. group_by=macom, series_by=fiscal_year
    //      → hierarchy_level=macom, group_by=fiscal_year
    //   2. group_by=fiscal_year, series_by=sub_agency
    //      → hierarchy_level=sub_agency, group_by=fiscal_year
    //      (swap: hierarchy becomes the level, flat dim becomes the group_by)
    //   3. group_by=macom, series_by=sub_agency
    //      → hierarchy_level=macom, group_by=fiscal_year (drop series; can't do two hierarchies)

    if (groupByIsHierarchy && !seriesByIsHierarchy) {
      // Case 1: primary is hierarchy, series is flat
      params.hierarchy_level = groupBy
      params.group_by = seriesBy || 'none'
    } else if (!groupByIsHierarchy && seriesByIsHierarchy) {
      // Case 2: primary is flat, series is hierarchy → swap
      params.hierarchy_level = seriesBy!
      params.group_by = groupBy
    } else {
      // Case 3: both are hierarchy → use primary, drop series
      params.hierarchy_level = groupBy
      params.group_by = 'fiscal_year'
    }
  } else {
    // Pure flat aggregation — group_by directly on fact columns
    const parts = [groupBy as string]
    if (seriesBy) parts.push(seriesBy as string)
    params.group_by = parts.join(',')
  }

  // Filters
  if (config.fiscal_year !== undefined) {
    params.fiscal_year = Array.isArray(config.fiscal_year)
      ? config.fiscal_year.join(',')
      : config.fiscal_year
  }
  if (config.fiscal_quarter) params.fiscal_quarter = config.fiscal_quarter
  if (config.sub_agency_code) params.sub_agency_code = config.sub_agency_code
  if (config.office_code) params.office_code = config.office_code
  if (config.psc_level1_code) params.psc_level1_code = config.psc_level1_code
  if (config.color_of_money) params.color_of_money = config.color_of_money
  if (config.macom_code) params.macom_code = config.macom_code
  if (config.agency_code) params.agency_code = config.agency_code
  if (config.portfolio_group) params.portfolio_group = config.portfolio_group
  if (config.vehicle_category) params.vehicle_category = config.vehicle_category
  if (config.extent_competed) params.extent_competed = config.extent_competed
  if (config.is_ot !== undefined) params.is_ot = config.is_ot
  if (config.sort_by) params.sort_by = config.sort_by
  if (config.limit) params.limit = config.limit

  // Include the active metric set in params so the in-memory cache key
  // differentiates by metric. The performance endpoint ignores this param
  // server-side (every rate column is always computed), but it keeps the
  // client-side cache from serving a stale row that's missing a column
  // added in a later backend release.
  if (config.metrics && config.metrics.length > 0) {
    params.metrics = config.metrics.join(',')
  }

  // Apply drill-down filter overrides (e.g., parent_code from clicking a bar)
  if (drillFilters) {
    Object.assign(params, drillFilters)
  }

  return params
}

/** Build params for the geo-performance endpoint. */
function buildGeoParams(
  config: GenericWidgetConfig,
  drillFilters?: Record<string, string | number>,
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {}

  const parts: string[] = [config.group_by as string]
  if (config.series_by) parts.push(config.series_by as string)
  params.group_by = parts.join(',')

  if (config.metrics && config.metrics.length > 0) {
    params.metrics = config.metrics.join(',')
  }

  if (config.location_kind) params.location_kind = config.location_kind
  if (config.state_code) {
    params.state_code = Array.isArray(config.state_code)
      ? config.state_code.join(',')
      : config.state_code
  }
  if (config.congressional_district) params.congressional_district = config.congressional_district
  if (config.psc_level1_code) params.psc_level1_code = config.psc_level1_code
  if (config.psc_level2_code) params.psc_level2_code = config.psc_level2_code
  if (config.color_of_money) params.color_of_money = config.color_of_money
  if (config.fiscal_year !== undefined) {
    params.fiscal_year = Array.isArray(config.fiscal_year)
      ? config.fiscal_year.join(',')
      : config.fiscal_year
  }
  if (config.sort_by) params.sort_by = config.sort_by
  if (config.limit) params.limit = config.limit

  if (drillFilters) {
    // Translate generic drill filters. Most drills at this grain produce
    // state_code / congressional_district / fiscal_year overrides, which the
    // endpoint accepts directly.
    Object.assign(params, drillFilters)
  }

  return params
}

export function usePerformanceQuery(
  config: GenericWidgetConfig,
  drillFilters?: Record<string, string | number>,
): UsePerformanceQueryResult {
  const [data, setData] = useState<PerfRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshCounter, setRefreshCounter] = useState(0)
  const generation = useRef(0)

  const paramsKey = useMemo(
    () => JSON.stringify({ config, drillFilters }),
    [config, drillFilters],
  )

  useEffect(() => {
    const gen = ++generation.current
    setLoading(true)
    setError(null)

    const params = buildParams(config, drillFilters)
    // On explicit refresh, bust both the frontend in-memory cache and the backend Redis cache
    if (refreshCounter > 0) {
      params.bust_cache = 1
    }

    const endpoint = isSpeedQuery(config)
      ? '/api/govdata/speed-percentiles/'
      : isGeoQuery(config)
      ? '/api/govdata/geo-performance/'
      : '/api/govdata/performance/'

    api
      .getRaw<PerfResponse>(endpoint, params)
      .then((resp) => {
        if (gen !== generation.current) return
        setData(resp.results)
        setLoading(false)
      })
      .catch((err) => {
        if (gen !== generation.current) return
        setError(err.message || 'Failed to load data')
        setLoading(false)
      })
  }, [paramsKey, refreshCounter])

  const refresh = useCallback(() => {
    api.clearCache()
    setRefreshCounter((c) => c + 1)
  }, [])

  return { data, loading, error, refresh }
}
